import axios, { type InternalAxiosRequestConfig } from 'axios';
import { browserHeaders } from '../features/auth/browserObservation';
import { askDeleteReason, deleteReasonTitle, needsDeleteReason, reasonPrompt, withReason } from './deleteReason';
import { ZAMOK_POLE, zamokProsit, zamokSlova } from './zamokZarplaty';
import { VKLADKA, hodBezArendy, resursAdresa, versiyaDlyaHoda, zapomnitVersiyu } from './vkladka';
import { kartochkaOtkryta, prinyatOtvetServera, vzyatNaHod } from '../features/aktualnost/reestr';

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.opsmind.ru/api/v1';

// Корень без /api/v1 — для /sanctum/csrf-cookie
const ROOT_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

/**
 * Полный адрес нашей двери по пути, который отдал сервер.
 *
 * ⛔ Адреса файлов сервер отдаёт путём («/vlozheniya/12»), а не полным URL:
 * панель и API живут на РАЗНЫХ доменах, и путь, вставленный в `<img src>` как
 * есть, ушёл бы на `admin.opsmind.ru` — то есть в пустоту. Склейка одна на
 * панель, чтобы адрес не собирали по месту каждый раз по-своему.
 */
export function adresApi(put: string): string {
  return `${API_BASE_URL}${put}`;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Cross-origin axios не добавляет X-XSRF-TOKEN сам (admin.opsmind.ru → api.opsmind.ru
 * — разные hosts, axios считает это cross-origin и в целях безопасности XSRF-header
 * не пишет). Делаем сами через interceptor: читаем XSRF-TOKEN из document.cookie
 * (Sanctum выставляет его с domain=.opsmind.ru, secure, без HttpOnly — то есть
 * виден из JS на admin.opsmind.ru) и кладём в заголовок мутирующих запросов.
 */
function readXsrfCookie(): string | null {
  const escaped = 'XSRF-TOKEN'.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Пометка запроса: аренда взята на время разового хода и после ответа отпускается. */
const RAZOVAYA = '__razovayaArenda';

function otpustitRazovuyu(config: unknown): void {
  const c = config as Record<string, unknown> | undefined;
  const otpustit = c?.[RAZOVAYA];
  if (typeof otpustit === 'function') {
    c![RAZOVAYA] = undefined;
    (otpustit as () => void)();
  }
}

apiClient.interceptors.request.use(async (config) => {
  for (const [key, value] of Object.entries(browserHeaders())) config.headers.set(key, value);
  const method = (config.method ?? 'get').toLowerCase();
  // `s713` по контракту `s712`: каждая вкладка называет себя на каждом запросе.
  config.headers.set('X-Vkladka', VKLADKA);
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const xsrf = readXsrfCookie();
    if (xsrf) {
      config.headers.set('X-XSRF-TOKEN', xsrf);
    }
    // Изменяющий ход по лиду и записи несёт версию, с которой работал экран (см. `vkladka.ts`).
    const adres = typeof config.url === 'string' ? config.url : '';
    const resurs = adres !== '' && !hodBezArendy(adres) ? resursAdresa(adres) : null;
    if (resurs !== null) {
      let versiya = versiyaDlyaHoda(resurs, kartochkaOtkryta(resurs));

      /*
       * `s719`, один редактор: менять можно только взятое на редактирование. Карточка открыта —
       * аренду ведёт она (`useRedaktor`). Карточки нет (перетаскивание на доске, кнопка в списке) —
       * аренда берётся на время этого хода и отпускается после ответа. Занято другим — дверь
       * аренды откажет, и ход не уйдёт: человек прочитает слово сервера, как при отказе самого хода.
       */
      const razovaya = await vzyatNaHod(resurs);
      if (razovaya !== null) {
        (config as unknown as Record<string, unknown>)[RAZOVAYA] = razovaya.otpustit;
        versiya ??= razovaya.versiya;
      }

      if (versiya !== null && !config.headers.has('X-Versiya')) {
        config.headers.set('X-Versiya', versiya);
      }
    }
  }
  // Прокидываем current_filial_id ТОЛЬКО для analytics endpoints.
  // Builder/Sites не должен зависеть от скрытого глобального фильтра филиала:
  // WithFilialScope на модели Site режет записи с filial_id=NULL, а переключатель
  // филиала скрыт от пользователя (FILIAL_PICKER_ENABLED=false) — пользователь
  // не может самостоятельно сбросить фильтр. Итог: /sites возвращает пустой список.
  const requestUrl = typeof config.url === 'string' ? config.url : '';
  const isAnalyticsEndpoint = requestUrl.startsWith('/analytics') || requestUrl.includes('/analytics/');
  if (isAnalyticsEndpoint) {
    try {
      const raw = localStorage.getItem('opsmind_current_filial_id');
      if (raw) {
        const parsed = JSON.parse(raw);
        const filialId = parsed?.state?.currentFilialId;
        if (typeof filialId === 'number') {
          config.headers.set('X-Filial-Id', String(filialId));
        }
      }
    } catch {
      // localStorage недоступен / парс упал — игнорируем
    }
  }
  return config;
});

/**
 * aud2 Д-5 — отказ «нужна причина» превращается в вопрос и повтор запроса.
 *
 * Ядро больше не записывает удаление без причины отметкой «причина не указана» —
 * оно ОТКАЗЫВАЕТ (422 с полем `reason`). Панель могла бы показать этот отказ как
 * ошибку и оставить кнопку мёртвой; вместо этого она спрашивает то, что попросил
 * сервер, и повторяет ход. Один перехватчик вместо поля причины в тридцати девяти
 * экранах — и ни одного лишнего вопроса там, где причина не нужна: спрашиваем
 * ровно по отказу, а разделы называет само ядро.
 *
 * Повтор помечается флагом: если сервер откажет снова (причина слишком коротка,
 * право не то), ошибка уходит наверх как обычно, а не крутит диалог по кругу.
 */
/** Версия ресурса из ответа: поле `versiya` разбора или заголовок `X-Versiya` успешного хода. */
function zapomnitVersiyuOtveta(url: unknown, zagolovok: unknown, telo: unknown): void {
  const resurs = typeof url === 'string' ? resursAdresa(url) : null;
  if (resurs === null) return;

  const izTela = telo !== null && typeof telo === 'object' ? (telo as { versiya?: unknown }).versiya : undefined;
  zapomnitVersiyu(resurs, typeof zagolovok === 'string' && zagolovok !== '' ? zagolovok : izTela);
}

/**
 * ХОД ПО АДРЕСУ, КОТОРЫЙ РЕСУРСА НЕ НАЗЫВАЕТ (`s719`): номер лида лежит в теле запроса или у
 * другой сущности — например статус заявки на экране «Лиды с форм» ведёт колонку лида. Клиент по
 * адресу не знает, чью аренду брать; сервер в строгом режиме отвечает «нужна аренда» (423
 * `arenda.nuzhna`) либо «нужна версия» (428 `versiya.nuzhna`) и НАЗЫВАЕТ ресурс в отказе. Клиент
 * берёт аренду на время хода, ставит версию и повторяет ход один раз. Занято другим — отказ
 * доходит до экрана как есть.
 */
async function povtoritSArendoy(error: unknown): Promise<unknown | null> {
  const e = error as { config?: InternalAxiosRequestConfig & Record<string, unknown>; response?: { status?: number; data?: { kod?: string; versiya?: string; arenda?: { tip?: string; id?: number } } } };
  const config = e?.config;
  const dannye = e?.response?.data;
  const kod = dannye?.kod;

  if (!config || config.__povtorSArendoy || (kod !== 'arenda.nuzhna' && kod !== 'versiya.nuzhna')) return null;
  // Адрес ресурс называет — аренду и версию клиент уже поставил сам; повтор ничего не изменит.
  if (typeof config.url !== 'string' || resursAdresa(config.url) !== null) return null;

  const tip = dannye?.arenda?.tip;
  const id = dannye?.arenda?.id;
  if (typeof tip !== 'string' || typeof id !== 'number') return null;

  const resurs = `${tip}:${id}`;
  let razovaya: Awaited<ReturnType<typeof vzyatNaHod>>;
  try {
    razovaya = await vzyatNaHod(resurs);
  } catch (otkazArendy) {
    // Лид редактирует другой: до экрана доходит этот отказ, со словом «кто и с какого времени».
    return Promise.reject(otkazArendy);
  }

  const versiya = versiyaDlyaHoda(resurs, kartochkaOtkryta(resurs)) ?? razovaya?.versiya ?? dannye?.versiya ?? null;
  config.__povtorSArendoy = true;
  if (razovaya !== null) config[RAZOVAYA] = razovaya.otpustit;
  if (versiya !== null) config.headers.set('X-Versiya', versiya);

  return apiClient.request(config);
}

/** Состояние аренды из ответа доезжает до открытой карточки сразу (разбор лида, отказ стража). */
function peredatArenduKartochke(url: unknown, telo: unknown, otkaz: boolean): void {
  const resurs = typeof url === 'string' ? resursAdresa(url) : null;
  if (resurs !== null) prinyatOtvetServera(resurs, telo, otkaz);
}

apiClient.interceptors.response.use((response) => {
  zapomnitVersiyuOtveta(response.config?.url, response.headers?.['x-versiya'], response.data);
  peredatArenduKartochke(response.config?.url, response.data, false);
  otpustitRazovuyu(response.config);

  return response;
});

/** Двери входа: их 401 — обычный ответ «не вошли», а не истёкший вход посреди работы. */
function dverVhoda(url: unknown): boolean {
  return typeof url === 'string' && /^\/?auth\//.test(url);
}

/**
 * ⛔ `s1027` G-13: ИСТЁКШИЙ ВХОД — СЛОВАМИ И С ПОВТОРОМ, А НЕ «Unauthenticated.».
 *
 * 419 — устарел ключ защиты формы (CSRF): ключ берётся заново и ход повторяется
 * ОДИН раз, человек ничего не замечает и набранное не теряет. 401 (или 419 после
 * повтора) — вход истёк: отказ помечается `sessiyaIstekla`, экран говорит
 * «войдите заново», а личность (`['auth','me']`) перечитывается — и панель сама
 * уводит на вход, если сервер это подтвердит.
 */
async function istyokshiyVhod(error: unknown): Promise<unknown | null> {
  const e = error as { config?: InternalAxiosRequestConfig & Record<string, unknown>; response?: { status?: number } };
  const status = e?.response?.status;
  const config = e?.config;

  if ((status !== 401 && status !== 419) || dverVhoda(config?.url)) return null;

  if (status === 419 && config && !config.__csrfPovtor) {
    config.__csrfPovtor = true;
    try {
      await ensureCsrfCookie();

      return apiClient.request(config);
    } catch (povtor) {
      // Повтор тоже отказан (или ключ не взялся) — до экрана доходит последний отказ.
      return Promise.reject(povtor);
    }
  }

  (error as { sessiyaIstekla?: boolean }).sessiyaIstekla = true;
  const qc = (window as Window & { __queryClient?: { invalidateQueries: (f: { queryKey: readonly unknown[] }) => Promise<void> } }).__queryClient;
  void qc?.invalidateQueries({ queryKey: ['auth', 'me'] });

  return null;
}

apiClient.interceptors.response.use(undefined, async (error) => {
  // Отказ стража тоже несёт текущую версию — после него экран перечитывает ресурс.
  zapomnitVersiyuOtveta(error?.config?.url, undefined, error?.response?.data);
  peredatArenduKartochke(error?.config?.url, error?.response?.data, true);
  otpustitRazovuyu(error?.config);

  const vhod = await istyokshiyVhod(error);
  if (vhod !== null) return vhod;
  if ((error as { sessiyaIstekla?: boolean })?.sessiyaIstekla === true) return Promise.reject(error);

  const povtor = await povtoritSArendoy(error);
  if (povtor !== null) return povtor;

  const config = error?.config;
  const status = error?.response?.status;
  const data = error?.response?.data;

  /* ⛔ `s966`: замок зарплаты у руководителя — та же форма вопроса, слова замка
     от сервера, повтор хода с `zamok_prichina`. Повтор помечен: второй отказ
     уходит на экран, диалог по кругу не крутится. Не руководству (`otkaz`)
     вопрос не задаётся — отказ идёт на экран как есть. */
  if (config && !config.__zamokAsked && zamokProsit(status, data)) {
    const prichina = await askDeleteReason(zamokSlova(data), deleteReasonTitle(config.method));

    if (prichina === null) return Promise.reject(error);

    config.__zamokAsked = true;
    config.data = withReason(config.data, prichina, ZAMOK_POLE);

    return apiClient.request(config);
  }

  if (!config || config.__reasonAsked || !needsDeleteReason(status, data)) {
    return Promise.reject(error);
  }

  /* ⛔ `s322` Д15: диалог называет РОД ДЕЙСТВИЯ, а не всегда удаление. Правка
     времени, отвергнутая без причины, спрашивала «Почему удаляем?» — и владелец
     читал отказ о ходе, которого не делал. */
  const reason = await askDeleteReason(reasonPrompt(data), deleteReasonTitle(config.method));

  if (reason === null) {
    // Человек передумал — это законный исход хода, а не сбой.
    return Promise.reject(error);
  }

  config.__reasonAsked = true;
  config.data = withReason(config.data, reason);

  return apiClient.request(config);
});

/**
 * Однократно запросить XSRF-cookie перед мутацией. Идемпотентно.
 */
export async function ensureCsrfCookie(): Promise<void> {
  await axios.get(`${ROOT_URL}/sanctum/csrf-cookie`, { withCredentials: true });
}
